/**
 * BulkActionBar — prototype-style selection/action rail above the table.
 *
 * Actions wired in V1.0.x:
 *   - Send reminders (fires `invoices.send` for each selected ≠ draft/void)
 *   - Clear selection
 *
 * Deferred (V1.x): CSV export.
 *
 * Removed: "Mark as paid" was previously a disabled placeholder. Recording
 * a receipt requires per-invoice attributes a bulk action can't reasonably
 * collect — date, mode, reference, amount, TDS, FX rate, settlement lines,
 * allocations. The right per-row affordance is "Record receipt" on the
 * invoice detail page; a bulk button would either be misleading or expand
 * into a per-row modal stack. Skipped.
 */
import { Download, MessageSquare } from "lucide-react";

export interface BulkActionBarProps {
  selectedCount: number;
  onSendReminders: () => void;
  onClear: () => void;
  /** True while the per-row send mutations are in flight. */
  busy?: boolean;
}

export function BulkActionBar({
  selectedCount,
  onSendReminders,
  onClear,
  busy,
}: BulkActionBarProps) {
  return (
    <div
      role="region"
      aria-label="Bulk actions"
      className="flex min-h-14 flex-wrap items-center gap-3 rounded-md bg-ink px-4 py-2.5 text-bg shadow-sm"
    >
      <span className="text-sm">
        {selectedCount} invoice{selectedCount === 1 ? "" : "s"} selected
      </span>
      <span className="ml-auto" />
      <button
        type="button"
        onClick={onSendReminders}
        disabled={selectedCount === 0 || busy}
        className="inline-flex h-9 items-center gap-2 rounded-md bg-white/10 px-3 text-sm font-medium text-white transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <MessageSquare size={14} />
        <span>{busy ? "Sending…" : "Send reminders"}</span>
      </button>
      <button
        type="button"
        disabled
        title="CSV export lands in V1.x."
        className="inline-flex h-9 cursor-not-allowed items-center gap-2 rounded-md bg-white/10 px-3 text-sm font-medium text-white opacity-50"
      >
        <Download size={14} />
        <span>Export</span>
      </button>
      <button
        type="button"
        onClick={onClear}
        className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-white/80 transition hover:bg-white/10 hover:text-white"
      >
        Cancel
      </button>
    </div>
  );
}
